import axios from 'axios';
import { open_session_modal, close_session_modal, show_modal_error } from './session_modal';
import {
  SESSION_EXPIRY_KEY,
  clear_auth_storage,
  has_auth_context,
} from './session_storage';
import {
  parse_response_payload,
  response_message,
  response_session_expiry_time,
} from './session_response';

const SESSION_WARNING_OFFSET_MS = 2 * 60 * 1000;
const SESSION_REFRESH_URL = process.env.VUE_APP_SESSION_REFRESH_URL || '';
const DEFAULT_SESSION_TIMEOUT_MINUTES = Number(process.env.VUE_APP_SESSION_TIMEOUT_MINUTES || 0);

const AUTH_EXPIRY_MESSAGE_PARTS = [
  'authentication credentials not provided',
  'credentials',
  'session',
  'expired',
  'unauthorized',
  'forbidden',
];

let router_instance = null;
let store_instance = null;
let interceptor_installed = false;
let warning_timer = null;
let expiry_timer = null;
let refresh_promise = null;

function is_auth_expiry_error(error) {
  const status = Number(error?.response?.status || 0);

  if (status === 401) {
    return true;
  }

  if (status !== 403) {
    return false;
  }

  const message = response_message(error).toLowerCase();
  return AUTH_EXPIRY_MESSAGE_PARTS.some((part) => message.includes(part));
}

function friendly_session_error(error) {
  if (error?.response) {
    error.response.data = {
      message: 'Your session has expired. Please choose an option to continue.',
    };
  }
  error.__session_expired = true;
  return error;
}

function clear_auth_timers() {
  if (warning_timer) {
    clearTimeout(warning_timer);
    warning_timer = null;
  }
  if (expiry_timer) {
    clearTimeout(expiry_timer);
    expiry_timer = null;
  }
}

function redirect_to_login(message = 'Your session has expired. Please sign in again.') {
  clear_auth_timers();
  clear_auth_storage();
  sessionStorage.setItem('auth_redirect_reason', message);

  if (store_instance) {
    store_instance.dispatch('resetStore');
  }

  if (router_instance?.currentRoute?.value?.name !== 'UserLogin') {
    router_instance.push({ name: 'UserLogin' });
  }
}

async function refresh_session() {
  if (!SESSION_REFRESH_URL) {
    throw new Error('No session refresh endpoint is configured.');
  }

  if (!refresh_promise) {
    refresh_promise = axios.post(
      SESSION_REFRESH_URL,
      {},
      { skipSessionExpiryHandler: true, withCredentials: true }
    ).finally(() => {
      refresh_promise = null;
    });
  }

  const response = await refresh_promise;
  const payload = parse_response_payload(response?.data);

  if (payload?.token) {
    sessionStorage.setItem('token', payload.token);
  }

  set_session_expiry_from_response(payload);
  return payload;
}

function extend_local_session() {
  if (DEFAULT_SESSION_TIMEOUT_MINUTES <= 0) {
    throw new Error('No session timeout is configured.');
  }

  const token = sessionStorage.getItem('token');
  if (!token) {
    throw new Error('No active session token was found.');
  }

  set_session_expiry_from_response({
    expires_in: DEFAULT_SESSION_TIMEOUT_MINUTES * 60,
  });
}

function retry_original_request(retry_request, original_error) {
  if (!retry_request || !original_error?.config) {
    return null;
  }
  return axios({
    ...original_error.config,
    _sessionRetry: true,
  });
}

function reject_session_error(error, original_error) {
  if (original_error) {
    throw friendly_session_error(original_error);
  }
  throw error;
}

async function renew_local_session_or_redirect({ retry_request, original_error } = {}) {
  close_session_modal();

  try {
    extend_local_session();
    return await retry_original_request(retry_request, original_error);
  } catch (session_error) {
    show_modal_error('Please sign in again to continue.');
    redirect_to_login('Your session has expired. Please sign in again.');
    return reject_session_error(session_error, original_error);
  }
}

function renew_remote_session_or_redirect({ retry_request, original_error } = {}) {
  return refresh_session()
    .then(() => {
      close_session_modal();
      return retry_original_request(retry_request, original_error);
    })
    .catch((refresh_error) => {
      show_modal_error('We could not renew your session. Please sign in again.');
      close_session_modal();
      redirect_to_login('We could not renew your session. Please sign in again.');
      throw friendly_session_error(refresh_error);
    });
}

async function renew_or_redirect(options = {}) {
  if (!SESSION_REFRESH_URL) {
    return renew_local_session_or_redirect(options);
  }
  return renew_remote_session_or_redirect(options);
}

async function handle_session_choice(original_error) {
  const choice = await open_session_modal(
    () => redirect_to_login('Your session has expired. Please sign in again.')
  );

  if (choice === 'logout') {
    close_session_modal();
    redirect_to_login('You have been logged out.');
    throw friendly_session_error(original_error);
  }

  return renew_or_redirect({ retry_request: true, original_error });
}

async function handle_scheduled_session_prompt(title) {
  const choice = await open_session_modal(
    () => redirect_to_login('Your session has expired. Please sign in again.'),
    { title }
  );

  if (choice === 'logout') {
    close_session_modal();
    redirect_to_login('You have been logged out.');
    return;
  }

  await renew_or_redirect().catch(() => null);
}

export function set_session_expiry_from_response(response_data = {}) {
  const expiry_time = response_session_expiry_time(
    response_data,
    DEFAULT_SESSION_TIMEOUT_MINUTES
  );

  if (!Number.isFinite(expiry_time) || expiry_time <= Date.now()) {
    return;
  }

  sessionStorage.setItem(SESSION_EXPIRY_KEY, String(expiry_time));
  schedule_session_timers();
}

export function schedule_session_timers() {
  clear_auth_timers();
  const expiry_time = Number(sessionStorage.getItem(SESSION_EXPIRY_KEY) || 0);
  if (!expiry_time || expiry_time <= Date.now()) {
    return;
  }

  const warning_delay = Math.max(expiry_time - Date.now() - SESSION_WARNING_OFFSET_MS, 0);
  const expiry_delay = Math.max(expiry_time - Date.now(), 0);

  warning_timer = setTimeout(() => {
    handle_scheduled_session_prompt('Session Expiring Soon');
  }, warning_delay);

  expiry_timer = setTimeout(() => {
    handle_scheduled_session_prompt('Session Expired');
  }, expiry_delay);
}

export function install_session_expiry_handler({ router, store } = {}) {
  router_instance = router;
  store_instance = store;
  schedule_session_timers();

  if (interceptor_installed) {
    return;
  }

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error?.config?.skipSessionExpiryHandler
        || error?.config?._sessionRetry
        || !has_auth_context(error?.config)
        || !is_auth_expiry_error(error)
      ) {
        return Promise.reject(error);
      }

      return handle_session_choice(error);
    }
  );

  interceptor_installed = true;
}

export { clear_auth_storage };
